import React, { useState } from "react";
import { Folder, FolderOpen, FolderPlus, Pencil, Trash2, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FolderRow = ({ f, active, onSelect, onReload, canEdit, canManage }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(f.name);
  const rename = async () => {
    try { await api.put(`/doc-folders/${f.id}`, { name }); toast.success("Pasta renomeada"); setEditing(false); onReload(name, f.name); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm(`Excluir a pasta "${f.name}"?`)) return;
    try { await api.delete(`/doc-folders/${f.id}`); toast.success("Pasta excluída"); onReload(null, f.name); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1 px-1 py-1">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename()} className="h-8 text-xs" autoFocus data-testid={`folder-rename-input-${f.id}`} />
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={rename} data-testid={`folder-rename-save-${f.id}`}><Check className="h-3.5 w-3.5 text-emerald-600" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(false); setName(f.name); }}><X className="h-3.5 w-3.5" /></Button>
      </div>
    );
  }
  return (
    <div className={`group flex items-center gap-1 rounded-lg pr-1 transition-colors ${active ? "bg-[#0B2545] text-white" : "hover:bg-slate-100"}`}>
      <button type="button" onClick={() => onSelect(active ? null : f.name)} className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left text-xs font-medium" data-testid={`folder-item-${f.id}`}>
        {active ? <FolderOpen className="h-4 w-4 shrink-0 text-emerald-400" /> : <Folder className="h-4 w-4 shrink-0 text-amber-500" />}
        <span className="truncate" title={f.name}>{f.name}</span>
        <span className={`ml-auto rounded-full px-1.5 text-[10px] ${active ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{f.count}</span>
      </button>
      {canEdit && <button type="button" onClick={() => setEditing(true)} className="hidden h-6 w-6 items-center justify-center rounded group-hover:flex" title="Renomear" data-testid={`folder-rename-${f.id}`}><Pencil className="h-3 w-3" /></button>}
      {canManage && <button type="button" onClick={remove} className="hidden h-6 w-6 items-center justify-center rounded text-rose-500 group-hover:flex" title="Excluir pasta vazia" data-testid={`folder-delete-${f.id}`}><Trash2 className="h-3 w-3" /></button>}
    </div>
  );
};

export function DocFolders({ folders, selected, onSelect, onReload }) {
  const { canEdit, canManage } = useAuth();
  const [name, setName] = useState("");
  const total = folders.reduce((s, f) => s + (f.count || 0), 0);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/doc-folders", { name: name.trim() }); toast.success("Pasta criada"); setName(""); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <aside className="h-fit rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-sm" data-testid="doc-folders">
      <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Pastas</p>
      <button type="button" onClick={() => onSelect(null)} className={`mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold ${!selected ? "bg-emerald-50 text-emerald-700" : "hover:bg-slate-100"}`} data-testid="folder-all">
        <FolderOpen className="h-4 w-4" /> Todos os documentos <span className="ml-auto rounded-full bg-slate-200 px-1.5 text-[10px] text-slate-600">{total}</span>
      </button>
      <div className="max-h-[420px] space-y-0.5 overflow-y-auto">
        {folders.map((f) => <FolderRow key={f.id} f={f} active={selected === f.name} onSelect={onSelect} onReload={onReload} canEdit={canEdit} canManage={canManage} />)}
      </div>
      {canEdit && (
        <div className="mt-3 flex gap-1 border-t border-slate-100 pt-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nova pasta" className="h-8 text-xs" data-testid="folder-new-input" />
          <Button size="sm" onClick={add} className="h-8 bg-[#0B2545] hover:bg-[#134074]" data-testid="folder-new-btn"><FolderPlus className="h-4 w-4" /></Button>
        </div>
      )}
    </aside>
  );
}
